<template>
  <div class="filters">
    <search-panel
      @input="handleSearchInput"
      @direction="handleDirectionInput" />

    <filter-panel
      class="filters-panel"
      :apply-filter-immediately="false"
      :filter-parameters="formattedFilters"
      @update="applyFilter"
      :notification-text="null"
      :api-request-data="null">

      <!-- refresh/auto-refresh feature -->
      <template #panel-actions>
        <div class="request-controls">
          <b-form-checkbox
            class="checkbox"
            @input="() => {}"
            v-model="autoRefresh"
            :disabled="!!getActiveRequestsCount">
            Auto-refresh
          </b-form-checkbox>
          <b-button :disabled="!!getActiveRequestsCount"
                    @click="forceRefreshFilter"
                    class="button"
                    size="sm"
                    :variant="refreshButtonVariant">
            Refresh
          </b-button>
        </div>
      </template>

    </filter-panel>
  </div>
</template>

<script>
import getNested from 'lodash-es/get'
import moment from 'moment'
import sortBy from 'lodash-es/sortBy'
import cloneDeep from 'lodash-es/cloneDeep'

import { mapActions, mapGetters } from 'vuex'

import FilterPanel, {
  getCurrentFilterValues,
  applyFilterValues,
  getAPIFilterValues,
} from '../widgets/FilterPanel'

import {
  getCurrentQuarterRange,
  getCurrentYear,
  getToday,
  getYearToDateRange
} from '@/shared/date-utils'

import SearchPanel from './SearchPanel'


const algoliaBaseParameters = {
  'getRankingInfo': true,
  'analytics': false,
  'enableABTest': false,
  'hitsPerPage': 50,
  'attributesToRetrieve': '*',
  'attributesToSnippet': '*:20',
  'snippetEllipsisText': '…',
  'responseFields': '*',
  'page': 0,
  'facets': ['*'],
};


export default {
  name: 'filters',

  components: {
    SearchPanel,
    FilterPanel,
  },

  props: {
    selectedBerths: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      direction: 'export',
      portId: [],
      filtersReady: false,
      autoRefresh: true,
      updatePending: false,
      searchIndex: 'polygons_prod',

      filterSchema: {
        excludeIntraCountry: {
          name: 'excludeIntraCountry',
          apiName: 'excludeIntraCountry',
          type: 'checkbox',
          label: 'Exclude intra country voyages',
          value: true,
          defaultChoice: true,
        },

        excludeUnknownDestinations: {
          name: 'excludeUnknownDestinations',
          apiName: 'excludeUnknownDestinations',
          type: 'checkbox',
          label: 'Exclude voyages with unknown destination',
          value: false,
          defaultChoice: false,
        },

        excludeParcel: {
          name: 'excludeParcel',
          apiName: 'parceling',
          type: 'checkbox',
          label: 'Exclude parcel voyages',
          value: false,
          defaultChoice: false,
        },

        berthId: {
          name: 'berthId',
          apiName: 'berthId',
          type: 'multiselect',
          label: 'Berth:',
          value: [],
          choices: [],
          options: {
            multiple: true,
            valueLabel: false,
            toggleAll: true,
          },
        },

        commodity: {
          name: 'commodity',
          apiName: 'commodity',
          type: 'treeselect',
          placeholder: 'Commodity',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        subSegment: {
          name: 'subSegment',
          apiName: 'subSegment',
          type: 'treeselect',
          placeholder: 'Segment',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        originRegion: {
          name: 'originRegion',
          apiName: 'fromRegion',
          type: 'multiselect-region',
          placeholder: 'Origin Region',
          value: [],
          defaultChoice: null,
          choices: [],
          options: {
            toggleAll: true,
          },
        },

        originCountry: {
          name: 'originCountry',
          apiName: 'fromCountryCode',
          type: 'multiselect',
          placeholder: 'Origin country',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        destinationRegion: {
          name: 'destinationRegion',
          apiName: 'toRegion',
          type: 'multiselect-region',
          placeholder: 'Destination Region',
          value: [],
          defaultChoice: null,
          choices: [],
          options: {
            toggleAll: true,
          },
        },

        destinationCountry: {
          name: 'destinationCountry',
          apiName: 'toCountryCode',
          type: 'multiselect',
          placeholder: 'Destination country',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        dateRange: {
          name: ['startDate', 'endDate'],
          type: 'daterange-picker',
          options: {
            ranges: this.getDatePickerRanges(),
          },

          startDate: {
            name: 'startDate',
            apiName: 'fromDate',
            value: moment('2020-01-01').format('YYYY-MM-DD'),
          },

          endDate: {
            name: 'endDate',
            apiName: 'toDate',
            value: moment().format('YYYY-MM-DD')
          },
        },
      },
    };
  },

  methods: {
    ...mapActions('api', ['algoliaSearch']),

    ...mapActions('api/tradeflows', [
      'getCommodities',
      'getSegments',
      'getCountries',
      'getTradeflowsRegionsWithPolygons',
      'portSearch',
    ]),

    async loadFilters() {
      await this.loadRegions();
      await this.loadCommodities();
      await this.loadSegments();
      this.filtersReady = true;
    },

    async loadRegions() {
      this.filterSchema.originCountry.choices = await this.getCountries();
      this.filterSchema.originRegion.choices = await this.getTradeflowsRegionsWithPolygons({direction: 'export'});
      this.filterSchema.destinationCountry.choices = await this.getCountries();
      this.filterSchema.destinationRegion.choices = await this.getTradeflowsRegionsWithPolygons({direction: 'import'});
    },

    async loadCommodities() {
      this.filterSchema.commodity.choices = await this.getCommodities({group: true});
    },

    async loadSegments() {
      this.filterSchema.subSegment.choices = await this.getSegments({group: true});
    },

    async handleSearchInput(searchData) {
      const portId = getNested(searchData, 'value.portId', null);
      this.direction = searchData.direction;

      if(portId) {
        this.portId = [portId];
        await this.loadBerthIdChoices(portId);
      } else {
        this.filterSchema.berthId.choices = [];
      }

      if(getNested(searchData, 'value.entityType') === 'PORT') {
        // if values contain port Id, select all berths in this port
        this.filterSchema.berthId.value = cloneDeep(this.filterSchema.berthId.choices);
      } else {
        // if values are berths, assign them to berthId value
        const berth = getNested(searchData, 'value');
        this.filterSchema.berthId.value = berth ? [cloneDeep({...berth, value: berth.berthId})] : [];
      }
    },

    async handleDirectionInput(parameters) {
      this.direction = parameters.direction;
    },

    async loadBerthIdChoices(portId) {
      const response = await this.algoliaSearch({
        searchIndex: this.searchIndex,
        query: '',
        searchParameters: {
          ...algoliaBaseParameters,
          filters: `port_id = ${portId} AND entity_type:"BERTH"`, // doesn't work (missing facet?)
        },
      });

      const results = getNested(response, 'hits', [])
        .map(item => {
          let coordinates;
          try {
            coordinates = getNested(JSON.parse(item.geom_point), 'coordinates', null)
          } catch(error) {
            coordinates = null;
          }

          return {
            value: item.berth_id,
            text: item.berth_name,
            id: `${item.entity_type}-${item.berth_id}`.toLowerCase(),
            coordinates: coordinates ? [coordinates[1], coordinates[0]] : null,
            radius: item.mbc_radius || null,
          };
      });

      this.filterSchema.berthId.choices = sortBy(results, item => item.text);
    },

    applyFilter(filterValues) {
      if(!this.filtersReady) {
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);

      if(this.autoRefresh) {
        this.$emit('search', this.getAPIFilterValues(filterValues));
        this.$emit('berths-loaded', this.getBerths());
        this.updatePending = false;
      } else {
        this.updatePending = true;
      }
    },

    forceRefreshFilter() {
      const filterValues = getCurrentFilterValues(this.filterSchema);
      this.$emit('search', this.getAPIFilterValues(filterValues));
      this.$emit('berths-loaded', this.getBerths());
      this.updatePending = false;
    },

    getAPIFilterValues(filterValues) {
      let apiFilterValues = getAPIFilterValues(this.filterSchema, filterValues);
      apiFilterValues.direction = this.direction;
      apiFilterValues.parceling = apiFilterValues.parceling ? 'exclude' : 'include';
      apiFilterValues.portId = this.portId;
      return apiFilterValues;
    },

    getBerths() {
      let selected;
      return this.filterSchema.berthId.choices.map(berth => {
        selected = this.filterSchema.berthId.value.find(
          item => item.value === berth.value
        );
        return { ...berth, selected: !!selected };
      });
    },

    getDatePickerRanges() {
      let ranges = {
        'Full period (2015-today)': [
          moment('2015-01-01').toDate(),
          getToday().toDate(),
        ],
        'QTD': getCurrentQuarterRange(),
        'YTD': getYearToDateRange(),
      };

      const currentYear = getCurrentYear();
      for(let year = currentYear - 3; year < currentYear; year++) {
        ranges[`${year}`]  = [
          moment(`${year}-01-01`).toDate(),
          moment(`${year}-12-31`).toDate(),
        ];
      }

      return ranges;
    },

    async applyFilterPreset(preset) {
      const portId = getNested(preset, 'params.searchPanel.searchValue.portId');
      const direction = getNested(preset, 'params.searchPanel.direction');

      if(portId) {
        this.portId = [portId];
        this.direction = direction;
        await this.loadBerthIdChoices(portId);
      }

      applyFilterValues(this.filterSchema, getNested(preset, 'params.filterPanel', {}));
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
    ...mapGetters('api/presets', ['getActivePreset']),

    refreshButtonVariant() {
      if(!this.autoRefresh && this.updatePending) {
        return 'info';
      } else {
        return 'outline-secondary';
      }
    },

    formattedFilters() {
      return {
        groups: [
          [
            this.filterSchema.berthId,
            this.filterSchema.subSegment,
            this.filterSchema.commodity,
          ],
          [
            this.filterSchema.originRegion,
            this.filterSchema.originCountry,
            this.filterSchema.destinationRegion,
            this.filterSchema.destinationCountry,
            this.filterSchema.dateRange,
          ],
          [
            this.filterSchema.excludeIntraCountry,
            this.filterSchema.excludeUnknownDestinations,
            this.filterSchema.excludeParcel,
          ],
        ],
      };
    },
  },

  watch: {
    direction() {
      let currentFilterValues = getCurrentFilterValues(this.filterSchema);
      this.applyFilter(currentFilterValues);
    },

    selectedBerths(berthIds) {
      this.filterSchema.berthId.value = berthIds.map(berthId => {
        return this.filterSchema.berthId.choices.find(
          berth => berth.value === berthId
        );
      });
    },

    autoRefresh(value) {
      if(value && this.updatePending) {
        this.forceRefreshFilter();
      }
    },

    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(!this.filtersReady) {
          await this.loadFilters();
          // if(!preset) {
          //   this.setDefaultValues();
          // }
        }

        if(preset) {
          await this.applyFilterPreset(this.getActivePreset);
        }
      },
    },
  },
}
</script>

<style scoped lang="scss">
.filters {
  margin-bottom: 1.5rem;
}

.request-controls {
  display: flex;
  flex-direction: column;
  margin: auto 0 5px 0;

  .checkbox {
    white-space: nowrap;
    margin-bottom: 5px;
  }
  .button {
    font-size: 0.85rem;
  }
}
</style>
